import { IonAlert } from "@ionic/react";
import { useContext, useEffect, useState } from "react";
import { initializeAbpSession, resetLoginAttempts } from "../api/abpLogin";
import { clearApiSession } from "../util/ApiOptionsHelper";
import { clearAuthData } from "../util/OAuth";
import { TranslationMessagesContext } from "../util/Translations";

interface ApiErrorProps {
    errorData: any;
    showRelogin?: boolean;
    showCancel?: boolean;
    showOk?: boolean;
    setShowLoading?: (loading: boolean) => void;    
    onOkOverride?: () => void | Promise<void>;
    onCancelOverride?: () => void | Promise<void>;
};

const ApiError: React.FC<ApiErrorProps> = (props) => {
    const { errorData, showRelogin, showCancel, showOk, setShowLoading } = props;
    const [errorText, setErrorText] = useState("");
    const [pendingLogout, setPendingLogout] = useState(false);
    
    useEffect(() => {
        setErrorText(errorData);
    }, [errorData]);

    const onReLogin = async () => {
        setErrorText("");
        if (setShowLoading) setShowLoading(true);
        await clearApiSession();
        window.location.href = "/AuthCallback";
    };

    const onOk = async () => {
        setErrorText("");
        if (setShowLoading) setShowLoading(true);
        await initializeAbpSession(true);
        window.location.href = "/Home";
    }

    // Cancel is only offered for the Named-licence question (resultCode 3), where
    // the server asks whether to terminate the user's other session. Declining is
    // a deliberate "no", not a login failure:
    //
    //  - Do NOT clear the ApiSession. It previously wiped the plant/database the
    //    user had just chosen, so the next attempt re-prompted for it.
    //  - Do NOT bounce through /AuthCallback. That re-runs authenticateUser(),
    //    which finds no BearerTokenPortal (a biometric restore only stores
    //    PortalApiToken) and calls signinPortal() - dropping the user on the
    //    AppCentral login page. A biometric session has no IdP browser cookie, so
    //    that sign-in is interactive, defeating the point of biometric sign-in.
    //    Returning to Identity keeps the biometric button available so the user
    //    can retry and answer OK.
    //  - Reset the attempt counter, so repeated declines cannot trip the
    //    "Too many attempts." lockout.
    const onCancel = async () => {
        setErrorText("");
        if (setShowLoading) setShowLoading(true);
        // Drop the EAM bearer token before navigating. Without this, UserIdentity's
        // "session still valid" gate (validateBearerToken() && isPortalTokenValid())
        // is still satisfied on arrival at "/" and redirects straight back to
        // /AuthCallback - where biometricRestored has already been consumed, so
        // getPortalBearerToken() returns null, signinPortal() runs and the user
        // lands on the AppCentral login page anyway. Declining the question means
        // no session was established, so holding an EAM token is wrong regardless.
        // clearAuthData() removes only BearerToken/BearerTokenParsed/
        // CurrentB2CScope - the IAM preference, biometricEnabled, the Keystore
        // credential, ApiSession (plant/DB) and PortalApiToken all survive, so
        // Identity renders with the biometric button and the user can retry.
        await clearAuthData();
        await resetLoginAttempts();
        window.location.href = "/";
    }

    // When no explicit button props are provided and the message requires re-login,
    // redirect to login on OK instead of just dismissing.
    // Match all session-expired variants used across the codebase:
    //   "relogin", "re-login", "session has expired", and the translated SessionExpired text.
    const { translatedMessages } = useContext(TranslationMessagesContext);
    const sessionExpiredMsg = translatedMessages["SessionExpired"]?.MessageText || "";
    const errorStr = errorText?.toString() || "";
    const errorLower = errorStr.toLowerCase().trim();
    const requiresRelogin = !showOk && !showCancel && !showRelogin &&
        (errorLower.includes("relogin") ||
            errorLower.includes("re-login") ||
            (!globalThis.abpRevalidateSession && errorLower.includes("session has expired")) ||
            (!!sessionExpiredMsg && errorStr === sessionExpiredMsg));

    const onSessionExpiredOk = () => {
        globalThis.relogin = true;
        setPendingLogout(true);
        setErrorText("");
    };

    const alertButtons = [
        ...(showOk ? [{ text: "OK", handler: () => { onOk(); } }] : []),
        ...(showCancel ? [{ text: "Cancel", handler: () => { onCancel(); } }] : []),
        ...(showRelogin ? [{ text: "Retry Login", handler: () => { onReLogin(); } }] : []),
        ...(requiresRelogin ? [{ text: "OK", handler: () => { onSessionExpiredOk(); } }] : []),
    ];

    return (
        <IonAlert
            isOpen={!!errorText}
            message={errorText?.toString()}
            backdropDismiss={false}
            buttons={alertButtons.length > 0 ? alertButtons : ["OK"]}
            onDidDismiss={() => {
                setErrorText("");
                if (pendingLogout) {
                    setPendingLogout(false);
                    window.location.href = "/logout?relogin=true";
                }
            }}
        />
    );
};

export default ApiError;
